import React, { createContext, useReducer, ReactNode, useEffect } from 'react';
import { appStateReducer } from './AppReducer';
import { ChatHistoryLoadingState, CosmosDBHealth, historyList, historyEnsure, CosmosDBStatus, PromptTemplateOverrideInfo, getAvailableModels, ModelInfo, ChatMessage, LoginUserInfo, DocumentSearchToolArg, getGeneratingConversations, GeneratingConversationInfo } from '../api';
import { Conversation, RAGMenuItem, RAGMenuList, getGroupDefault } from '../api';
import { syncCacheSelectedRAGIds } from './LocalStorageCache';
import { SideBarState } from '../pages/sidebar/Sidebar';
import { useIsMobile } from '../hooks/useIsMobile';
  
export interface AppState {
    isSideBarOpen: boolean;
    sideBarState: SideBarState
    isRAGEnabled: boolean;
    selectedRAGIds: Set<number>;
    documentSearchToolArg: DocumentSearchToolArg
    chatHistoryLoadingState: ChatHistoryLoadingState;
    isCosmosDBAvailable: CosmosDBHealth;
    chatHistory: Conversation[] | null;
    filteredChatHistory: Conversation[] | null;
    currentChat: Conversation | null;
    generatingConversations: Map<string, GeneratingConversationInfo>; // task_id -> conversation_id
    RAGMenuList: RAGMenuItem[] | null;
    selectedGPTModelIndex: string | null;
    promptTemplateOverride: null | PromptTemplateOverrideInfo;
    tools: string[];
    availableModels: Map<string, ModelInfo>;
    loginUserInfo: LoginUserInfo | null;
}

export type Action =
    | { type: 'SET_SIDEBAR_OPEN', payload: boolean }
    | { type: 'SET_SIDEBAR_STATE', payload: SideBarState }
    | { type: 'SET_IS_RAG_ENABLED', payload: {isEnabled: boolean, openRAGMenuIfToggleToOpen: boolean} }
    | { type: 'TOGGLE_MOBILE_RAG_MENU' } // モバイル版RAGメニュー表示用に追加
    | { type: 'SET_RAG_CHAT', payload: Set<number> }
    | { type: 'SET_DOCUMENT_SEARCH_TOOL_ARG', payload: DocumentSearchToolArg }
    | { type: 'SET_COSMOSDB_STATUS', payload: CosmosDBHealth }
    | { type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState }
    | { type: 'UPDATE_CURRENT_CHAT', payload: Conversation | null }
    | { type: 'UPDATE_FILTERED_CHAT_HISTORY', payload: Conversation[] | null }
    | { type: 'UPDATE_CHAT_HISTORY', payload: Conversation } // API Call
    | { type: 'UPDATE_GENERATING_CONVERSATIONS', payload: Map<string, GeneratingConversationInfo> }
    | { type: 'UPDATE_CHAT_TITLE', payload: Conversation } // API Call
    | { type: 'CHANGE_CHAT_FAVORITE', payload: Conversation } // API Call
    | { type: 'DELETE_CHAT_ENTRY', payload: string } // API Call
    | { type: 'DELETE_CHAT_HISTORY', payload: Set<number>}  // API Call
    | { type: 'DELETE_CURRENT_CHAT_MESSAGES', payload: string }  // API Call
    | { type: 'FETCH_CHAT_HISTORY', payload: Conversation[] | null }  // API Call
    | { type: 'APPEND_CHAT_HISTORY', payload: Conversation[] }  // API Call(追加読み込み)
    | { type: 'FETCH_CHAT_HISTORY_MESSAGES', payload: {convId: string, messages: ChatMessage[]} }
    | { type: 'FETCH_RAG_MENU_LIST', payload: RAGMenuItem[] | null }  // API Call
    | { type: 'SET_SELECTED_GPT_MODEL_INDEX', payload: string | null } 
    | { type: 'SET_MESSAGE_FEEDBACK', payload: {convId: string, mesId: string, feedback: string}}
    | { type: 'SET_PROMPT_TEMPLATE_OVERRIDE', payload: null | PromptTemplateOverrideInfo } 
    | { type: 'SET_TOOLS', payload: string[] } 
    | { type: 'SET_AVAILABLE_MODELS', payload: Map<string, ModelInfo> } 
    | { type: 'SET_LOGIN_USER_INFO', payload: LoginUserInfo}

const initialState: AppState = {
    isSideBarOpen: false,
    sideBarState: "ChatHistory",
    isRAGEnabled: false,
    selectedRAGIds: new Set<number>(),
    documentSearchToolArg: {tag_options: new Map()},
    chatHistoryLoadingState: ChatHistoryLoadingState.Loading,
    chatHistory: null,
    filteredChatHistory: null,
    currentChat: null,
    generatingConversations: new Map<string, GeneratingConversationInfo>(),
    isCosmosDBAvailable: {
        cosmosDB: false,
        status: CosmosDBStatus.NotConfigured,
    },
    RAGMenuList: null,
    selectedGPTModelIndex: null,
    promptTemplateOverride: null,
    tools: [],
    availableModels: new Map<string, ModelInfo>(),
    loginUserInfo: null,
};

export const AppStateContext = createContext<{
    state: AppState;
    dispatch: React.Dispatch<Action>;
  } | undefined>(undefined);

type AppStateProviderProps = {
    children: ReactNode;
  };
  
  export const AppStateProvider: React.FC<AppStateProviderProps> = ({ children }) => {
    const [state, dispatch] = useReducer(appStateReducer, initialState)
    const isMobile = useIsMobile()

    useEffect(() => {
        dispatch({ type: "SET_SIDEBAR_OPEN", payload: !isMobile })

        const getHistoryEnsure = async () => {
            dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Loading });
            historyEnsure().then((response) => {
                if(response?.cosmosDB){
                    dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Success });
                    dispatch({ type: 'SET_COSMOSDB_STATUS', payload: response });
                }else{
                    dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail });
                    dispatch({ type: 'SET_COSMOSDB_STATUS', payload: response });
                }
            })
            .catch((err) => {
                dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail });
                dispatch({ type: 'SET_COSMOSDB_STATUS', payload: {cosmosDB: false, status: CosmosDBStatus.NotConfigured} });
            })
        }
        getHistoryEnsure();

        const fetchRAGMenuList = async (): Promise<RAGMenuItem[] | null> => {
            const result = await RAGMenuList().then((response) => {
                if(response){
                    // RAGインデックスが取得できたタイミングで、localStorageに保存された選択状況を現状のインデックス状況と整合させる
                    const availableRAGIds = response.map(v => Number(v.id))
                    if(availableRAGIds) {
                        syncCacheSelectedRAGIds(availableRAGIds)
                    }
                    console.info("FETCH_RAG_MENU_LIST")
                    dispatch({ type: 'FETCH_RAG_MENU_LIST', payload: response});
                }else{
                    console.info("FETCH_RAG_MENU_LIST null")
                    dispatch({ type: 'FETCH_RAG_MENU_LIST', payload: null });
                }
                return response
            })
            .catch((err) => {
                console.error("There was an issue fetching your data. fetchRAGMenuList");
                console.error(err);
                return null
            })
            return result
        }
        fetchRAGMenuList();

        const fetchAvailableModels = async () => {
            const result = await getAvailableModels()
            if (result) {
                dispatch({ type: "SET_AVAILABLE_MODELS", payload: result });
            }
        }
        fetchAvailableModels()

        const fetchDefaultValue = async () => {
            const result = await getGroupDefault()
            // URLパスに conversationId が含まれていない場合のみRAGメニューを開く
            const hasConversationId = window.location.pathname.includes('/chat/');
            if (result.rag_flag === true && !hasConversationId) {
                dispatch({ type: 'SET_IS_RAG_ENABLED', payload: {isEnabled: true, openRAGMenuIfToggleToOpen: true} });
            }
            dispatch({ type: 'SET_SELECTED_GPT_MODEL_INDEX', payload: result.default_model_index  });
        }
        fetchDefaultValue();

        const fetchGeneratingConversations = async () => {
            const result = await getGeneratingConversations();
            const data = new Map<string, GeneratingConversationInfo>()
            for (const conv of result) {
                data.set(conv.task_id, conv)
            }
            dispatch({ type: 'UPDATE_GENERATING_CONVERSATIONS', payload: data });
        }
        fetchGeneratingConversations();

    }, []);

  
    return (
      <AppStateContext.Provider value={{ state, dispatch }}>
        {children}
      </AppStateContext.Provider>
    );
  };


